import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import { getSupabase } from '@/lib/supabase';
import Logo from '@/components/feature/Logo';

interface CalendarEntry {
  time: string;
  status: string;
  appointment_id: string | null;
  client_name: string | null;
  reference_number: string | null;
  client_phone: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface AppointmentRow {
  id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  created_at: string;
  updated_at: string;
  application_id: string;
  completed_at: string | null;
  applications: {
    id: string;
    reference_number: string;
    client_full_name: string;
    client_phone: string;
    client_email: string;
    country_of_travel: string;
    visa_type: string;
    application_type: string;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  confirmed: { label: 'Confirmed', color: 'text-green-700', bg: 'bg-green-100' },
  cancelled: { label: 'Cancelled', color: 'text-foreground-500', bg: 'bg-secondary-200' },
  completed: { label: 'Completed', color: 'text-foreground-600', bg: 'bg-secondary-100' },
};

type StatusTab = 'confirmed' | 'cancelled' | 'completed' | 'today' | 'upcoming' | 'calendar';

const TAB_OPTIONS: { key: StatusTab; label: string; icon: string }[] = [
  { key: 'calendar', label: 'Daily Calendar', icon: 'ri-calendar-line' },
  { key: 'today', label: 'Today', icon: 'ri-sun-line' },
  { key: 'upcoming', label: 'Upcoming', icon: 'ri-arrow-right-line' },
  { key: 'confirmed', label: 'Confirmed', icon: 'ri-check-line' },
  { key: 'cancelled', label: 'Cancelled', icon: 'ri-close-line' },
  { key: 'completed', label: 'Completed', icon: 'ri-check-double-line' },
];

export default function AdminAppointments() {
  const { staffName, logout } = useAdminAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<StatusTab>('calendar');
  const [showMoreTabs, setShowMoreTabs] = useState(false);

  // Calendar tab
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [calendar, setCalendar] = useState<CalendarEntry[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(true);

  // List tabs
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [listLoading, setListLoading] = useState(false);

  // Shared state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const showFeedback = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 5000);
  };

  // Fetch calendar
  const fetchCalendar = useCallback(async () => {
    setCalendarLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { data, error: fnErr } = await supabase.functions.invoke(
        `manage-appointments?action=daily-calendar&date=${encodeURIComponent(selectedDate)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (fnErr) throw fnErr;
      setCalendar(data?.slots || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load calendar');
    } finally {
      setCalendarLoading(false);
    }
  }, [selectedDate]);

  // Fetch list
  const fetchList = useCallback(async (statusOrAction: string) => {
    setListLoading(true);
    setError('');
    try {
      const supabase = getSupabase();

      if (statusOrAction === 'today') {
        const { data } = await supabase.functions.invoke(
          'manage-appointments?action=today',
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        setAppointments(data?.appointments || []);
      } else if (statusOrAction === 'upcoming') {
        const { data } = await supabase.functions.invoke(
          'manage-appointments?action=upcoming',
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        setAppointments(data?.appointments || []);
      } else {
        const { data } = await supabase.functions.invoke(
          `manage-appointments?status=${encodeURIComponent(statusOrAction)}`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        setAppointments(data?.appointments || []);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendar();
    } else {
      fetchList(activeTab);
    }
  }, [activeTab, fetchCalendar, fetchList]);

  // Cancel
  const handleCancel = async (appointmentId: string) => {
    if (!window.confirm('Cancel this appointment? The time slot will become available.')) return;
    setSaving(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { data, error: fnErr } = await supabase.functions.invoke('manage-appointments?action=cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'cancel', appointment_id: appointmentId }),
      });

      if (fnErr) {
        setError(fnErr instanceof Error ? fnErr.message : 'Edge function error');
        return;
      }
      if (data?.error) { setError(data.error); return; }
      showFeedback('Appointment cancelled.');
      if (activeTab === 'calendar') fetchCalendar();
      else fetchList(activeTab);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setSaving(false);
    }
  };

  // Complete
  const handleComplete = async (appointmentId: string) => {
    setSaving(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { data, error: fnErr } = await supabase.functions.invoke('manage-appointments?action=complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'complete', appointment_id: appointmentId }),
      });

      if (fnErr) {
        setError(fnErr instanceof Error ? fnErr.message : 'Edge function error');
        return;
      }
      if (data?.error) { setError(data.error); return; }
      showFeedback('Appointment marked as completed.');
      if (activeTab === 'calendar') fetchCalendar();
      else fetchList(activeTab);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getStatusBadge = (status: string) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: 'text-foreground-600', bg: 'bg-secondary-100' };
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>;
  };

  const calendarStats = {
    confirmed: calendar.filter((e) => e.status === 'confirmed').length,
    available: calendar.filter((e) => e.status === 'available').length,
  };

  const visibleTabs = TAB_OPTIONS.filter((t) =>
    t.key === 'calendar' || t.key === activeTab || ['today', 'upcoming', 'confirmed'].includes(t.key)
  );

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-primary-900 sticky top-0 z-40">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-3">
              <Logo variant="light" className="w-[130px] md:w-[170px]" />
              <span className="hidden sm:block font-heading font-bold text-sm md:text-base text-background-50 whitespace-nowrap">PVS Admin</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-background-50/80 text-sm">{staffName}</span>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-background-50/10 text-background-50 text-xs font-medium rounded-md border border-background-50/20 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">Logout</button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-background-50 cursor-pointer">
                <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 flex flex-col gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
          )}
        </div>
      </header>

      <main className="py-6 md:py-10">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Appointment Management</h2>
              <p className="text-sm text-foreground-600 mt-1">View confirmed office appointments — cancel or mark as completed.</p>
            </div>
          </div>

          {feedback && (
            <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2">
              <i className="ri-check-line"></i> {feedback}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2">
              <i className="ri-error-warning-line"></i> {error}
              <button onClick={() => setError('')} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Dismiss</button>
            </div>
          )}

          {/* Tab bar */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex-shrink-0 px-4 py-2 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  activeTab === t.key ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                }`}
              >
                <i className={`${t.icon} text-sm`}></i>
                {t.label}
              </button>
            ))}
            <button
              onClick={() => setShowMoreTabs(!showMoreTabs)}
              className="flex-shrink-0 px-3 py-2 text-xs font-medium rounded-full bg-secondary-100 text-foreground-600 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-more-line text-sm"></i>
            </button>
          </div>

          {/* Dropdown for "more" tabs */}
          {showMoreTabs && (
            <div className="flex flex-wrap gap-1 mb-4 p-3 bg-secondary-50 border border-secondary-200 rounded-lg">
              {TAB_OPTIONS.filter((t) => !visibleTabs.find((vt) => vt.key === t.key)).map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setActiveTab(t.key); setShowMoreTabs(false); }}
                  className={`px-4 py-2 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                    activeTab === t.key ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-600 hover:bg-secondary-100 border border-secondary-200'
                  }`}
                >
                  <i className={`${t.icon} text-sm`}></i>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Calendar View */}
          {activeTab === 'calendar' && (
            <div className="space-y-6">
              <div className="bg-background-50 border border-secondary-200 rounded-lg p-4 md:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-foreground-700 whitespace-nowrap">Select Date:</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="px-4 py-2 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                    />
                    <button onClick={fetchCalendar} className="w-8 h-8 flex items-center justify-center bg-secondary-100 text-foreground-600 rounded-md hover:bg-secondary-200 transition-colors cursor-pointer">
                      <i className="ri-refresh-line text-sm"></i>
                    </button>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-100 border border-green-300"></span> <span className="text-foreground-600">{calendarStats.confirmed} Booked</span></span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-secondary-100 border border-secondary-300"></span> <span className="text-foreground-600">{calendarStats.available} Available</span></span>
                  </div>
                </div>
              </div>

              {calendarLoading ? (
                <div className="text-center py-16">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
                  <p className="text-sm text-foreground-500 mt-3">Loading calendar...</p>
                </div>
              ) : (
                <div className="bg-background-50 border border-secondary-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-4 border-b border-secondary-200">
                    <h3 className="font-heading font-semibold text-base text-foreground-900">{formatDate(selectedDate)}</h3>
                  </div>
                  {calendar.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto flex items-center justify-center bg-secondary-200 rounded-full mb-3">
                        <i className="ri-calendar-close-line text-2xl text-foreground-500"></i>
                      </div>
                      <p className="text-sm text-foreground-600 font-medium">No appointments for this date</p>
                      <p className="text-xs text-foreground-500 mt-1">All 16 time slots are available for booking.</p>
                    </div>
                  ) : (
                    <div className="max-h-[600px] overflow-y-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-secondary-200 bg-secondary-50">
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Time Slot</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Status</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Client</th>
                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap hidden md:table-cell">Reference</th>
                            <th className="text-right px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calendar.map((entry) => {
                            const isBooked = entry.status === 'confirmed';
                            const isAvailable = entry.status === 'available';

                            return (
                              <tr key={entry.time} className={`border-b border-secondary-100 transition-colors ${isBooked ? 'bg-green-50/30' : ''}`}>
                                <td className="px-4 py-3 text-sm font-medium whitespace-nowrap text-foreground-800">
                                  {entry.time}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {isBooked && getStatusBadge('confirmed')}
                                  {isAvailable && <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-foreground-500">Available</span>}
                                </td>
                                <td className="px-4 py-3 text-sm text-foreground-800 whitespace-nowrap">
                                  {entry.client_name || '—'}
                                  {entry.client_phone && <span className="block text-xs text-foreground-500">{entry.client_phone}</span>}
                                </td>
                                <td className="px-4 py-3 text-sm text-foreground-600 whitespace-nowrap hidden md:table-cell">{entry.reference_number || '—'}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  {isBooked && entry.appointment_id && (
                                    <div className="flex items-center justify-end gap-1.5">
                                      <button onClick={() => handleCancel(entry.appointment_id!)} disabled={saving} className="px-2 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-md hover:bg-red-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer" title="Cancel">
                                        <i className="ri-close-line"></i>
                                      </button>
                                      <button onClick={() => handleComplete(entry.appointment_id!)} disabled={saving} className="px-2 py-1.5 bg-secondary-100 text-foreground-700 text-xs font-medium rounded-md hover:bg-secondary-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer" title="Mark Done">
                                        <i className="ri-check-double-line"></i>
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* List Views */}
          {activeTab !== 'calendar' && (
            <div>
              {listLoading ? (
                <div className="text-center py-16">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
                  <p className="text-sm text-foreground-500 mt-3">Loading...</p>
                </div>
              ) : appointments.length === 0 ? (
                <div className="bg-background-50 border border-secondary-200 rounded-lg p-12 text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center bg-secondary-200 rounded-full mb-4">
                    <i className="ri-check-double-line text-2xl text-foreground-500"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">No Appointments Found</h3>
                  <p className="text-sm text-foreground-500">There are no appointments in this category right now.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((appt) => (
                    <div key={appt.id} className="bg-background-50 border border-secondary-200 rounded-lg p-4 md:p-5">
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center flex-wrap gap-2 mb-2">
                            {getStatusBadge(appt.status)}
                            <span className="text-sm font-medium text-primary-700">{appt.applications?.reference_number || '—'}</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                            <div>
                              <p className="text-xs text-foreground-500">Client</p>
                              <p className="text-sm font-medium text-foreground-900">{appt.applications?.client_full_name || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-foreground-500">Contact</p>
                              <p className="text-sm text-foreground-700">{appt.applications?.client_phone || '—'} · {appt.applications?.client_email || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-foreground-500">Travel</p>
                              <p className="text-sm text-foreground-700">{appt.applications?.country_of_travel || '—'} · {appt.applications?.visa_type || '—'} Visa</p>
                            </div>
                            <div>
                              <p className="text-xs text-foreground-500">Appointment</p>
                              <p className="text-sm font-semibold text-foreground-900">{appt.appointment_date} {appt.appointment_time}</p>
                            </div>
                            <div>
                              <p className="text-xs text-foreground-500">Submitted</p>
                              <p className="text-sm text-foreground-600">{appt.created_at?.split('T')[0] || '—'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 lg:flex-shrink-0">
                          {appt.status === 'confirmed' && (
                            <>
                              <button onClick={() => handleComplete(appt.id)} disabled={saving} className="px-4 py-2 bg-secondary-100 text-foreground-700 text-sm font-medium rounded-md hover:bg-secondary-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap">
                                <i className="ri-check-double-line mr-1"></i>Done
                              </button>
                            </>
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && (
                            <button onClick={() => handleCancel(appt.id)} disabled={saving} className="px-4 py-2 bg-red-100 text-red-700 text-sm font-medium rounded-md hover:bg-red-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap">
                              <i className="ri-close-line mr-1"></i>Cancel
                            </button>
                          )}
                          <Link to={`/admin/client/${appt.application_id}`} className="px-4 py-2 bg-secondary-100 text-foreground-700 text-sm font-medium rounded-md hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-eye-line mr-1"></i>View Client
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}